import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type } from "arktype";
import { CreditCard, Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";

import { orpc } from "@/client";
import { PersonSearch, PersonSearchSchemas } from "@/components/medico/person-search";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { OverlayShell } from "@/components/ui/overlay";
import { MAX_CARD_INSTALLMENTS } from "@/lib/card-installments";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { format } from "@/lib/format";

import { FinanceiroCurrencyInput } from "../financeiro-currency-input";
import { BankMovementContext } from "./bank-movement-context";
import type { ReceiptPrefill } from "./types";

const receiptForm = type({
	person: PersonSearchSchemas.chosen.or("null"),
	"attendance_id?": "number.integer > 0 | null",
	method: type.enumerated("pix", "transfer", "cash", "debit_card", "credit_card", "other"),
	amount_cents: "number.integer > 0",
	received_on: "string",
	"declared_installment_count?": type(`1 <= number.integer <= ${MAX_CARD_INSTALLMENTS}`),
	description: "string",
}).narrow((value, ctx) => {
	if (value.person === null) {
		return ctx.mustBe("a person");
	}

	return (
		(value.method !== "credit_card" && value.method !== "other") ||
		value.declared_installment_count !== undefined ||
		ctx.mustBe("an installment count")
	);
});

type ReceiptForm = typeof receiptForm.infer;
type CardMethod = Extract<ReceiptForm["method"], "debit_card" | "credit_card" | "other">;

type ReceiptSource =
	| { kind: "none" }
	| {
			kind: "bank";
			bank: NonNullable<ReceiptPrefill["bank"]>;
			method: "pix" | "transfer";
			amountCents: number;
			receivedOn: string;
	  }
	| {
			kind: "card";
			cardSaleId: number;
			method: CardMethod;
			amountCents: number;
			receivedOn: string;
	  };

function initialPerson(initial: ReceiptPrefill | undefined) {
	if (initial?.person !== undefined) {
		return initial.person;
	}

	if (typeof initial?.person_id === "number" && typeof initial.person_name === "string") {
		return {
			kind: "patient" as const,
			patientId: initial.person_id,
			sendpulseId: null,
			name: initial.person_name,
		};
	}

	return null;
}

function receiptSource(initial: ReceiptPrefill | undefined): ReceiptSource {
	if (initial?.bank !== undefined) {
		const method =
			initial.bank.method?.toLocaleLowerCase("pt-BR").includes("pix") === true
				? ("pix" as const)
				: ("transfer" as const);

		return {
			kind: "bank",
			bank: initial.bank,
			method,
			amountCents: initial.bank.amount_cents,
			receivedOn: initial.bank.effective_on,
		};
	}

	if (initial?.card_sale_id !== undefined) {
		return {
			kind: "card",
			cardSaleId: initial.card_sale_id,
			method: initial.method,
			amountCents: initial.amount_cents,
			receivedOn: initial.received_on,
		};
	}

	return { kind: "none" };
}

function guidanceFor(source: ReceiptSource, correcting: boolean) {
	if (source.kind === "bank") {
		return "Selecione quem pagou. A entrada bancária será vinculada automaticamente ao recebimento.";
	}

	if (source.kind === "card") {
		return "Selecione quem pagou. A venda da Rede será vinculada automaticamente ao recebimento.";
	}

	if (correcting) {
		return "A correção substitui o registro atual sem apagar o histórico.";
	}

	return "Informe quem pagou. O comprovante será associado quando já estiver disponível.";
}

function ReceiptSourceContext({
	source,
	description,
}: {
	source: ReceiptSource;
	description: string | null | undefined;
}) {
	if (source.kind === "bank") {
		return <BankMovementContext movement={source.bank} />;
	}

	if (source.kind !== "card") {
		return null;
	}

	return (
		<div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-background px-3 py-2.5">
			<div className="flex min-w-0 items-center gap-3">
				<CreditCard className="size-4 shrink-0 text-primary" />
				<div className="min-w-0">
					<p className="text-xs font-medium text-muted-foreground">Venda da Rede</p>
					<p className="break-words text-sm font-semibold text-foreground">
						{description ?? "Venda no cartão"}
					</p>
					<p className="mt-1 text-xs text-muted-foreground">
						{format.date(source.receivedOn)}, {FinanceiroLabels.receipt.method[source.method]}
					</p>
				</div>
			</div>
			<p className="shrink-0 font-mono text-sm font-semibold text-foreground">
				{FinanceiroLabels.money(source.amountCents)}
			</p>
		</div>
	);
}

function PaymentMethodOptions({ source }: { source: ReceiptSource }) {
	if (source.kind !== "none") {
		return <option value={source.method}>{FinanceiroLabels.receipt.method[source.method]}</option>;
	}

	return Object.entries(FinanceiroLabels.receipt.method).map(([key, label]) => (
		<option key={key} value={key}>
			{label}
		</option>
	));
}

export function ReceiptModal({
	initial,
	onClose,
}: {
	initial?: ReceiptPrefill;
	onClose: () => void;
}) {
	const queryClient = useQueryClient();
	const source = receiptSource(initial);
	const person = initialPerson(initial);
	const evidenceLocked = source.kind !== "none";
	const defaultMethod = source.kind === "none" ? (initial?.method ?? "pix") : source.method;
	const defaultAmount = source.kind === "none" ? (initial?.amount_cents ?? 0) : source.amountCents;
	const defaultDate =
		source.kind === "none" ? (initial?.received_on ?? format.today()) : source.receivedOn;
	const create = useMutation(
		orpc.financeiro.receipts.create.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: orpc.financeiro.key(),
				});
				toast.success(
					initial?.replaces_id === undefined ? "Recebimento registrado" : "Recebimento corrigido",
				);
				onClose();
			},
			onError: () => toast.error("Não foi possível registrar o recebimento"),
		}),
	);
	const close = () => {
		if (!create.isPending) {
			onClose();
		}
	};
	const {
		control,
		register,
		handleSubmit,
		setValue,
		trigger,
		formState: { isValid },
	} = useForm<ReceiptForm>({
		resolver: arktypeResolver(receiptForm),
		mode: "onChange",
		defaultValues: {
			person,
			attendance_id: initial?.attendance_id === undefined ? null : initial.attendance_id,
			method: defaultMethod,
			amount_cents: defaultAmount,
			received_on: defaultDate,
			declared_installment_count: initial?.declared_installment_count ?? 1,
			description: initial?.description ?? "",
		},
	});
	const selectedPerson = useWatch({ control, name: "person" });
	const selectedMethod = useWatch({ control, name: "method" });
	const receivedOn = useWatch({ control, name: "received_on" });
	const attendanceId = useWatch({ control, name: "attendance_id" });
	const patientId = selectedPerson?.kind === "patient" ? selectedPerson.patientId : null;
	const attendances = useQuery(
		orpc.financeiro.receipts.attendanceCandidates.queryOptions({
			input: { person_id: patientId ?? 0, received_on: receivedOn },
			enabled: patientId !== null,
		}),
	);
	useEffect(() => {
		void trigger();
	}, [selectedPerson, trigger]);
	useEffect(() => {
		if (patientId === null && attendanceId !== undefined && attendanceId !== null) {
			setValue("attendance_id", null, { shouldValidate: true });
			return;
		}

		if (
			attendanceId !== undefined &&
			attendanceId !== null &&
			attendances.data !== undefined &&
			!attendances.data.some((attendance) => attendance.id === attendanceId)
		) {
			setValue("attendance_id", null, { shouldValidate: true });
		}
	}, [attendanceId, attendances.data, patientId, setValue]);
	const submit = handleSubmit((value) => {
		if (value.person === null) {
			return;
		}

		const method = source.kind === "none" ? value.method : source.method;
		const amountCents = source.kind === "none" ? value.amount_cents : source.amountCents;
		const receivedOn = source.kind === "none" ? value.received_on : source.receivedOn;
		let declaredInstallmentCount: number | undefined;

		switch (method) {
			case "debit_card":
				declaredInstallmentCount = 1;
				break;
			case "credit_card":
			case "other":
				declaredInstallmentCount = value.declared_installment_count;
				break;
			case "cash":
			case "pix":
			case "transfer":
				break;
		}

		create.mutate({
			person_id: value.person.kind === "patient" ? value.person.patientId : value.person.leadId,
			attendance_id: value.attendance_id,
			method,
			amount_cents: amountCents,
			received_on: receivedOn,
			declared_installment_count: declaredInstallmentCount,
			description: value.description,
			replaces_id: initial?.replaces_id,
			bank_transaction_id: source.kind === "bank" ? source.bank.id : undefined,
			card_sale_id: source.kind === "card" ? source.cardSaleId : undefined,
		});
	});
	const correcting = initial?.replaces_id !== undefined;
	const guidance = guidanceFor(source, correcting);

	return (
		<OverlayShell
			label={correcting ? "Corrigir recebimento" : "Registrar recebimento"}
			onClose={close}
			className="items-center justify-center p-4"
		>
			<form
				data-component="financeiro-receipt-modal"
				aria-busy={create.isPending}
				onSubmit={submit}
				className="overlay-panel max-w-md gap-4 p-5"
			>
				<div>
					<h2 className="type-section-title text-foreground">
						{correcting && "Corrigir recebimento"}
						{!correcting && "Registrar recebimento"}
					</h2>
					<p className="mt-1 text-xs text-muted-foreground">{guidance}</p>
				</div>

				<ReceiptSourceContext source={source} description={initial?.description} />

				<label className="flex flex-col gap-1.5 text-sm font-medium">
					Paciente ou lead
					<Controller
						control={control}
						name="person"
						render={({ field }) => (
							<PersonSearch
								chosen={field.value}
								onChoose={field.onChange}
								disabled={create.isPending}
								placeholder="Buscar paciente ou lead por nome"
							/>
						)}
					/>
				</label>

				{patientId !== null && (
					<label className="flex flex-col gap-1.5 text-sm font-medium">
						Atendimento
						<Controller
							control={control}
							name="attendance_id"
							render={({ field }) => (
								<select
									value={field.value ?? ""}
									onChange={(event) =>
										field.onChange(event.target.value === "" ? null : Number(event.target.value))
									}
									disabled={create.isPending || attendances.isLoading}
									className="control-field h-11 md:h-10"
								>
									<option value="">Sem atendimento vinculado</option>
									{attendances.data?.map((attendance) => (
										<option key={attendance.id} value={attendance.id}>
											{format.date(attendance.start_at.slice(0, 10))}, {attendance.service_type}
										</option>
									))}
								</select>
							)}
						/>
						{!attendances.isLoading && attendances.data?.length === 0 && (
							<span className="text-xs font-normal text-muted-foreground">
								Nenhum atendimento próximo à data do recebimento.
							</span>
						)}
					</label>
				)}

				<label className="flex flex-col gap-1.5 text-sm font-medium">
					Forma de pagamento
					<select
						{...register("method")}
						disabled={create.isPending || evidenceLocked}
						className="control-field h-11 md:h-10"
					>
						<PaymentMethodOptions source={source} />
					</select>
				</label>

				<div className="grid grid-cols-2 gap-3">
					<label className="flex flex-col gap-1.5 text-sm font-medium">
						Valor
						<Controller
							control={control}
							name="amount_cents"
							render={({ field }) => (
								<FinanceiroCurrencyInput
									valueCents={field.value}
									onValueCentsChange={field.onChange}
									disabled={create.isPending || evidenceLocked}
									slot="amount"
								/>
							)}
						/>
					</label>
					<label className="flex flex-col gap-1.5 text-sm font-medium">
						Data
						<Controller
							control={control}
							name="received_on"
							render={({ field }) => (
								<DatePicker
									value={field.value}
									onChange={(value) => value !== null && field.onChange(value)}
									disabled={create.isPending || evidenceLocked}
									containerClassName="w-full"
									className="w-full justify-between"
								/>
							)}
						/>
					</label>
				</div>

				{(selectedMethod === "credit_card" || selectedMethod === "other") && (
					<label className="flex flex-col gap-1.5 text-sm font-medium">
						Parcelas
						<Input
							{...register("declared_installment_count", {
								valueAsNumber: true,
								shouldUnregister: true,
							})}
							type="number"
							min={1}
							max={MAX_CARD_INSTALLMENTS}
							step={1}
							disabled={create.isPending}
						/>
					</label>
				)}

				<label className="flex flex-col gap-1.5 text-sm font-medium">
					Descrição
					<Input {...register("description")} disabled={create.isPending} placeholder="Opcional" />
				</label>

				<div className="sticky bottom-0 z-10 -mx-5 -mb-5 flex justify-end gap-2 border-t border-border bg-card px-5 pb-5 pt-3">
					<Button type="button" variant="ghost" disabled={create.isPending} onClick={close}>
						Cancelar
					</Button>
					<Button
						type="submit"
						disabled={selectedPerson === null || !isValid || create.isPending}
						data-slot="submit-receipt"
					>
						{create.isPending && <Loader2 className="animate-spin" />}
						{create.isPending && "Salvando"}
						{!create.isPending && correcting && "Salvar correção"}
						{!create.isPending && !correcting && "Registrar"}
					</Button>
				</div>
			</form>
		</OverlayShell>
	);
}
